import React, { useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CreditCard,
  Lock,
  Shield,
  Zap,
} from 'lucide-react';
import { authService } from '../services/auth.service.ts';
import { getUserPlan } from '../utils/plan.ts';

const PRO_PLAN = {
  id: 'pro',
  name: 'TBKMeet Pro',
  price: '279.90',
  period: 'ay',
  features: [
    'Sınırsız toplantı süresi',
    '100 katılımcı',
    '1440p görüntü kalitesi',
    'Bulut kayıt (10 GB)',
    'Bekleme odası',
    'Öncelikli destek',
  ],
};

function Checkout() {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'pro';
  const user = authService.getUser();
  const currentPlan = getUserPlan(user);
  const [submitted, setSubmitted] = useState(false);

  const selectedPlan = useMemo(() => {
    if (planId === 'pro') return PRO_PLAN;
    return null;
  }, [planId]);

  if (!selectedPlan) {
    return <Navigate to="/pricing" replace />;
  }

  if (currentPlan === 'pro') {
    return <Navigate to="/pricing" replace />;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 pb-16 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Fiyatlandırmaya dön
        </Link>

        <div className="flex items-center gap-2 text-green-400 text-sm mb-4">
          <Lock className="w-4 h-4" />
          <span>256-bit SSL ile güvenli ödeme oturumu</span>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h1 className="text-2xl font-bold text-white mb-2">Güvenli Ödeme</h1>
            <p className="text-gray-400 text-sm mb-6">
              Ödeme altyapısı yakında aktif edilecek. Aşağıdaki form, canlı entegrasyon öncesi güvenli akışı test etmek içindir.
            </p>

            {submitted ? (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-300 text-sm">
                Ödeme isteğiniz alındı. Ödeme sağlayıcı entegrasyonu tamamlandığında bu ekrandan işlem yapılabilecek.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Kart üzerindeki isim</label>
                  <input
                    type="text"
                    required
                    autoComplete="cc-name"
                    placeholder="Ad Soyad"
                    className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Kart numarası</label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      required
                      inputMode="numeric"
                      autoComplete="cc-number"
                      placeholder="0000 0000 0000 0000"
                      className="w-full pl-11 pr-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">Son kullanma</label>
                    <input
                      type="text"
                      required
                      autoComplete="cc-exp"
                      placeholder="AA/YY"
                      className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">CVV</label>
                    <input
                      type="password"
                      required
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      placeholder="•••"
                      className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/30 transition"
                >
                  {selectedPlan.price} ₺ / {selectedPlan.period} — Ödemeyi tamamla
                </button>
              </form>
            )}

            <div className="mt-6 flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> KVKK uyumlu
              </span>
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> Kart bilgisi saklanmaz (test)
              </span>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 h-fit">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{selectedPlan.name}</h2>
                <p className="text-gray-400 text-sm">Aylık abonelik</p>
              </div>
            </div>

            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-3xl font-bold text-white">{selectedPlan.price} ₺</span>
              <span className="text-gray-400">/ {selectedPlan.period}</span>
            </div>

            <ul className="space-y-2 mb-6">
              {selectedPlan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <p className="text-xs text-gray-500 leading-relaxed">
              İstediğiniz zaman iptal edebilirsiniz. Ödeme onayı sonrası planınız otomatik yükseltilecektir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
